/**
 * coursing.ts — Fitting a repeating masonry pattern onto a surface.
 *
 * Roof tiles and wall bricks are a **texture**, not geometry: one small
 * running-bond image, tiled across the shell of the building, with the windows
 * and doors as separate meshes on top. The alternative — an instance per tile —
 * was tried first and cost 150k triangles to draw something a 256×256 canvas
 * does better, because a tile that's a real box has to stand *off* the roof to
 * be visible at all, and at that size it reads as gravel stuck to the slope.
 *
 * What lives here is the pure arithmetic of making that texture land at the
 * right size, which is fiddly enough to be worth testing away from Three.js:
 *
 * - A texture tiles in UV space, but UV space is per-face and normalised. A
 *   140-wide wall and an 84-wide wall both run u from 0 to 1, so the same
 *   texture gives bricks two-thirds the size on one of them. Every face
 *   therefore needs its own **repeat count**, derived from its real extent.
 * - Those counts must be **whole numbers**, or the pattern is cut mid-brick
 *   where u wraps from 1 back to 0 — which on a closed box is the corner of
 *   the building, the most visible line on it.
 *
 * Everything here is in world units. `cell` is the size of one cell of the
 * pattern; the returned counts are how many cells fit.
 */

/** Repeat counts for one face: `u` across it, `v` up it. */
export interface Repeats {
  u: number;
  v: number;
}

/**
 * Whole cells across a run of `length`, never fewer than one.
 *
 * Rounding rather than flooring keeps the cell closest to the size asked for;
 * the floor at 1 stops a surface shorter than a single cell from losing its
 * pattern altogether.
 */
export function fitCells(length: number, cell: number): number {
  if (!(length > 0) || !(cell > 0)) return 1;
  return Math.max(1, Math.round(length / cell));
}

/**
 * Repeat counts for each of the three face pairs of a box.
 *
 * A box's six faces come in three pairs, and each pair spans a different two
 * dimensions, so each needs its own count. Which world axes a face's u and v
 * run along follows `RoundedBoxGeometry`'s own convention:
 *
 *   ±x   u along z, v along y
 *   ±y   u along x, v along z
 *   ±z   u along x, v along y
 */
export function boxRepeats(
  w: number,
  h: number,
  d: number,
  cellW: number,
  cellH: number,
): { x: Repeats; y: Repeats; z: Repeats } {
  return {
    x: { u: fitCells(d, cellW), v: fitCells(h, cellH) },
    y: { u: fitCells(w, cellW), v: fitCells(d, cellH) },
    z: { u: fitCells(w, cellW), v: fitCells(h, cellH) },
  };
}

/**
 * The same, for a box whose pattern is turned through a right angle.
 *
 * Used by the gable slopes: one slab shape serves a ridge running along x and
 * one running along z, and the courses have to follow the ridge either way, so
 * on one of them the pattern is laid sideways.
 *
 * Turning the pattern turns its **cell** too — what was the cell's width now
 * runs up the face, so the two sizes swap before they meet the extents. Getting
 * only the axes to swap and not the sizes is a quiet, ugly bug: the counts stay
 * plausible, so the roof still tiles, but with 42-unit tiles along the ridge
 * and 1.6-unit courses up the slope it reads as fine horizontal stripes with no
 * cross joints at all. Two of the town's four gables shipped like that.
 *
 * Counts come back in **final uv order** — `u` is the multiplier for the
 * turned u, which reads from the face's v axis — so the caller applies them the
 * same way in both cases.
 */
export function turnedBoxRepeats(
  w: number,
  h: number,
  d: number,
  cellW: number,
  cellH: number,
): { x: Repeats; y: Repeats; z: Repeats } {
  const r = boxRepeats(w, h, d, cellH, cellW);
  const turn = (f: Repeats): Repeats => ({ u: f.v, v: f.u });
  return { x: turn(r.x), y: turn(r.y), z: turn(r.z) };
}

/** One triangular slope of a hipped roof. */
export interface HipFace {
  /** Outward horizontal direction — which of the four sides this is. */
  ox: number;
  oz: number;
  /** Half the eave's length; the face spans ±this along its own eave. */
  halfEave: number;
  /** Horizontal distance from the centre out to the eave. */
  reach: number;
  /** True length from eave to apex, up the slope. */
  slant: number;
  /** Cells across the eave, and up the slope. */
  cells: Repeats;
}

/**
 * The four slopes of a hipped roof — a pyramid on a rectangular plan.
 *
 * Each slope rises from one eave to the shared apex, and on an oblong plan the
 * pair over the long sides is both wider and shallower than the pair over the
 * short ones. Sizing the tiles per face is what keeps them the same size all
 * the way round; parameterising the pyramid as a whole — which is what a cone's
 * own UVs do — stretches them across the wide faces in proportion to how
 * oblong the house is.
 */
export function hipFaces(
  w: number,
  d: number,
  height: number,
  cellW: number,
  cellH: number,
): HipFace[] {
  const rise = Math.max(height, 0);
  const sides = [
    { ox: 0, oz: 1, eave: w, reach: d / 2 },
    { ox: 0, oz: -1, eave: w, reach: d / 2 },
    { ox: 1, oz: 0, eave: d, reach: w / 2 },
    { ox: -1, oz: 0, eave: d, reach: w / 2 },
  ];

  return sides.map((s) => {
    const slant = Math.hypot(s.reach, rise);
    return {
      ox: s.ox,
      oz: s.oz,
      halfEave: s.eave / 2,
      reach: s.reach,
      slant,
      cells: { u: fitCells(s.eave, cellW), v: fitCells(slant, cellH) },
    };
  });
}

/** Raw triangle soup for a mesh: three positions and three UVs per face. */
export interface Triangles {
  positions: number[];
  uvs: number[];
}

/**
 * A hipped roof as four tiled triangles, ready to upload.
 *
 * Built by hand rather than by squashing a cone, because a cone's UVs
 * parameterise the whole pyramid at once — on an oblong house that hands the
 * long slopes the same slice of texture as the short ones and stretches their
 * tiles to match. Laying each face out separately keeps every tile on the roof
 * the same size, which is the entire point of working in world units.
 *
 * The apex vertex sits at the *middle* of its face's u range, so courses taper
 * to the point the way real hip tiles are cut.
 *
 * **Winding.** Each triangle runs eave-left → eave-right → apex, where "left"
 * is taken looking at the face from outside. That direction is
 * `outward × up = (oz, −ox)`, and it flips between opposite faces: use one
 * fixed along-eave axis for all four and two of them come out inside-out,
 * invisible from the street and lit from underneath.
 */
export function hipTriangles(
  w: number,
  d: number,
  height: number,
  cellW: number,
  cellH: number,
): Triangles {
  const out: Triangles = { positions: [], uvs: [] };

  for (const f of hipFaces(w, d, height, cellW, cellH)) {
    const ax = f.oz; // along the eave, left to right as seen from outside
    const az = -f.ox;
    const corner = (s: number): [number, number] => [
      f.ox * f.reach + ax * s * f.halfEave,
      f.oz * f.reach + az * s * f.halfEave,
    ];
    const [lx, lz] = corner(-1);
    const [rx, rz] = corner(1);

    out.positions.push(lx, 0, lz, rx, 0, rz, 0, Math.max(height, 0), 0);
    out.uvs.push(0, 0, f.cells.u, 0, f.cells.u / 2, f.cells.v);
  }
  return out;
}

/**
 * Dimensions of one plane of a gabled roof.
 *
 * The ridge follows the longer side, so each slope is a rectangle `ridge` long
 * and `slant` deep. Courses run along the ridge, which means the texture's u
 * has to follow `ridge` and its v climb `slant` — the other way round and the
 * tiles run *down* the slope like fluting.
 */
export function gableSlope(
  w: number,
  d: number,
  height: number,
): { alongX: boolean; ridge: number; span: number; slant: number; tilt: number } {
  const rise = Math.max(height, 0);
  const alongX = w >= d;
  const span = alongX ? d : w; // eave to eave, across the ridge
  const half = span / 2;
  return {
    alongX,
    ridge: alongX ? w : d,
    span,
    slant: Math.hypot(half, rise),
    tilt: Math.atan2(rise, half),
  };
}
